import IconArrowBack2 from "@/icons/IconArrowBack2";
import * as React from "react";
import { Link } from "react-router-dom";
import { StickyHeaderContext } from "@/context/StickyHeaderContext";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";

interface Props {
  title: string;
  subTitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  backLink?: string;
  tabs?: React.ReactNode;
}

export default function StickyRouteHeader(props: Props) {
  const { setHasStickyHeader, toggleSidebar } = React.useContext(StickyHeaderContext);
  const breadcrumb = useBreadcrumb();

  // Signal MainLayout synchronously (before paint) so it suppresses its standalone breadcrumb.
  React.useLayoutEffect(() => {
    setHasStickyHeader(true);
    return () => setHasStickyHeader(false);
  }, [setHasStickyHeader]);

  const px = toggleSidebar ? "px-6" : "px-16";
  const contentPad = toggleSidebar ? "p-3 sm:p-6 pt-4" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16 pt-4";

  return (
    <>
      <header
        className="shrink-0 sticky top-0 z-50 bg-white border-b border-zinc-200"
        style={{ isolation: "isolate" }}
      >
        {breadcrumb.length > 0 && (
          <nav
            aria-label="breadcrumb"
            className={`flex items-center gap-1 text-xs text-zinc-400 bg-white ${px} pt-3 pb-2`}
          >
            {breadcrumb.map((crumb, i) => {
              const isLast = i === breadcrumb.length - 1;
              return (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-zinc-300 mx-0.5">›</span>}
                  {isLast || !crumb.to ? (
                    <span className={isLast ? "text-zinc-600 font-medium" : "text-zinc-400"}>
                      {crumb.label}
                    </span>
                  ) : (
                    <Link to={crumb.to} className="hover:text-primary transition-colors">
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        )}

        <div className={`flex items-center justify-between py-3 ${px}`}>
          <div className="text-lg font-medium text-zinc-800 flex flex-row items-center">
            {props.backLink && (
              <span className="mr-2">
                <Link to={props.backLink} aria-label="Back">
                  <IconArrowBack2 />
                </Link>
              </span>
            )}
            <div className="flex flex-col">
              <h1 className="m-0 text-base font-semibold text-zinc-800 leading-snug">{props.title}</h1>
              {props.subTitle && (
                <span className="text-sm font-normal text-zinc-500 dark:text-zinc-300 mt-0.5">
                  {props.subTitle}
                </span>
              )}
            </div>
          </div>
          {props.headerActions && <span>{props.headerActions}</span>}
        </div>

        {props.tabs && (
          <nav aria-label="page tabs" className={`flex overflow-x-auto ${px}`}>
            {props.tabs}
          </nav>
        )}
      </header>

      {/* Content wrapper restores the padding that <main> no longer provides */}
      <div className={contentPad}>
        {props.children}
      </div>
    </>
  );
}
